import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grow,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  styled,
} from '@mui/material'
import {
  type Row,
  type SortingState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import clsx from 'clsx'
import { usePostHog } from 'posthog-js/react'
import { type FC, memo, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ListActions } from 'react-use/lib/useList'
import {
  type ItemProps,
  type ScrollerProps,
  type TableBodyProps,
  type TableComponents,
  type TableProps,
  TableVirtuoso,
} from 'react-virtuoso'
import IconMdiArrowDown from '~icons/mdi/arrow-down'
import IconMdiTrashCan from '~icons/mdi/trash-can'
import { BetaBadge } from '../components/global/BetaBadge'
import {
  type ComboFlag,
  type PlayEntry,
  RatingCalculatorAddEntryForm,
  type SyncFlag,
} from '../components/rating/RatingCalculatorAddEntryForm'
import { RatingCalculatorStatistics } from '../components/rating/RatingCalculatorStatistics'
import { ClearButton } from '../components/rating/io/ClearButton'
import { ExportMenu } from '../components/rating/io/ExportMenu'
import { ImportMenu } from '../components/rating/io/ImportMenu'
import { RenderToOneShotImageButton } from '../components/rating/io/export/RenderToOneShotImageButton'
import { useRatingEntries } from '../components/rating/useRatingEntries'
import { SheetListItem, SheetListItemContent } from '../components/sheet/SheetListItem'
import { useRatingCalculatorContext } from '../models/context/RatingCalculatorContext'
import { type FlattenedSheet, useSheets } from '../songs'
import type { Rating } from '../utils/rating'

export interface Entry {
  sheet: FlattenedSheet
  rating: Rating | null
  sheetId: string
  achievementRate: number
  comboFlag?: ComboFlag
  syncFlag?: SyncFlag
  includedIn: 'b15' | 'b35' | null
}

const columnHelper = createColumnHelper<Entry>()

const RatingCalculatorRowActions: FC<{
  modifyEntries: ListActions<PlayEntry>
  entry: PlayEntry
}> = ({ modifyEntries, entry }) => {
  const { data: sheets } = useSheets({ acceptsPartialData: true })
  const [dialogOpen, setDialogOpen] = useState(false)
  const posthog = usePostHog()
  const { t } = useTranslation(['rating-calculator'])

  const handleClick = useCallback(() => {
    modifyEntries.filter((existingEntry) => existingEntry.sheetId !== entry.sheetId)
  }, [])

  const sheet = useMemo(() => sheets?.find((sheet) => sheet.id === entry.sheetId), [sheets, entry.sheetId])

  return (
    <>
      <Dialog
        TransitionComponent={Grow}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        classes={{
          paper: 'min-w-[20rem]',
        }}
      >
        <DialogTitle>{t('rating-calculator:table.remove-dialog.title')}</DialogTitle>
        <DialogContent>{sheet && <SheetListItemContent sheet={sheet} />}</DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('rating-calculator:table.remove-dialog.cancel')}</Button>

          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setDialogOpen(false)
              handleClick()
              posthog?.capture('rating_calculator_remove_entry_button_clicked')
            }}
          >
            {t('rating-calculator:table.remove-dialog.remove')}
          </Button>
        </DialogActions>
      </Dialog>

      <IconButton size="small" onClick={() => setDialogOpen(true)}>
        <IconMdiTrashCan />
      </IconButton>
    </>
  )
}

const TransparentPaper = styled(Paper)(() => ({
  backgroundColor: 'transparent',
  boxShadow: 'none',
}))

export const RatingCalculator = () => {
  const { modifyEntries } = useRatingCalculatorContext()
  const { data: sheets } = useSheets({ acceptsPartialData: true })
  const [showOnlyB50, setShowOnlyB50] = useState(false)
  const [compactMode, setCompactMode] = useState(false)
  const { t } = useTranslation(['rating-calculator'])

  const { allEntries } = useRatingEntries()

  const onSubmit = useCallback(
    (entry: PlayEntry) => {
      if (allEntries.some((existingEntry) => existingEntry.sheetId === entry.sheetId)) {
        modifyEntries.updateFirst((existingEntry) => existingEntry.sheetId === entry.sheetId, entry)
      } else modifyEntries.push(entry)
    },
    [allEntries, modifyEntries],
  )

  if (!sheets) return null

  return (
    <div className="flex-container w-full pb-global">
      <div className="flex flex-col md:flex-row items-start gap-4 w-full">
        <Alert icon={false} severity="info" className="px-4 py-2 w-full md:w-2/3" classes={{ message: 'w-full' }}>
          <AlertTitle className="font-bold">{t('rating-calculator:breakdown.title')}</AlertTitle>
          <RatingCalculatorStatistics />
        </Alert>

        <div className="flex flex-col gap-4 h-full self-stretch w-full md:w-1/3">
          <Alert
            icon={false}
            severity="info"
            className="w-full overflow-auto px-4 py-2"
            classes={{
              message: 'w-full',
            }}
          >
            <AlertTitle className="font-bold">
              {allEntries?.length
                ? t('rating-calculator:auto-save.saved-records', { count: allEntries.length })
                : t('rating-calculator:auto-save.title')}
            </AlertTitle>

            <div className="mt-2">
              <RenderToOneShotImageButton />
            </div>

            <div className="flex items-center gap-2 mt-2">
              <ImportMenu modifyEntries={modifyEntries} />

              <ExportMenu />

              <div className="flex-1" />

              <ClearButton modifyEntries={modifyEntries} />
            </div>
          </Alert>

          <Alert
            icon={false}
            severity="info"
            className="w-full px-4 py-2"
            classes={{
              message: 'overflow-unset',
            }}
          >
            <AlertTitle className="font-bold">{t('rating-calculator:quick-actions.title')}</AlertTitle>
            <div className="flex flex-col items-start mt-2">
              <FormControlLabel
                control={<Switch checked={showOnlyB50} onChange={() => setShowOnlyB50((prev) => !prev)} />}
                label={t('rating-calculator:quick-actions.show-only-b50')}
              />

              <FormControlLabel
                control={<Switch checked={compactMode} onChange={() => setCompactMode((prev) => !prev)} />}
                label={
                  <div className="flex items-center gap-1 leading-none">
                    {t('rating-calculator:quick-actions.compact-mode')} <BetaBadge />
                  </div>
                }
              />
            </div>
          </Alert>
        </div>
      </div>

      <RatingCalculatorAddEntryForm onSubmit={onSubmit} />

      <div className="max-w-screen w-full overflow-x-auto -mx-4">
        <RatingCalculatorTableContent compactMode={compactMode} showOnlyB50={showOnlyB50} />

        {allEntries.length === 0 && (
          <div className="w-full text-sm py-8 px-4 text-center">{t('rating-calculator:table.no-entries')}</div>
        )}
      </div>
    </div>
  )
}

const RatingCalculatorIncludedInCell: FC<{
  row: Row<Entry>
}> = memo(({ row }) => {
  const includedIn = row.original.includedIn
  if (!includedIn) return null

  return (
    <div
      className={clsx(
        'tabular-nums font-mono tracking-tighter w-12 leading-none py-1.5 rounded-full text-white text-center shadow select-none',
        includedIn === 'b15' && 'bg-amber-500',
        includedIn === 'b35' && 'bg-cyan-500',
      )}
    >
      {includedIn.toUpperCase()}
    </div>
  )
})
RatingCalculatorIncludedInCell.displayName = 'memo(RatingCalculatorIncludedInCell)'

const COMBO_FLAG_CONFIG: Record<NonNullable<ComboFlag>, { label: string; color: string; rank: number }> = {
  fc: { label: 'FC', color: '#22bb5b', rank: 1 },
  fcp: { label: 'FC+', color: '#169b48', rank: 2 },
  ap: { label: 'AP', color: '#f5c31a', rank: 3 },
  app: { label: 'AP+', color: '#e5a800', rank: 4 },
}

const SYNC_FLAG_CONFIG: Record<NonNullable<SyncFlag>, { label: string; color: string; rank: number }> = {
  sync: { label: 'SYNC', color: '#64b5f6', rank: 1 },
  fs: { label: 'FS', color: '#42a5f5', rank: 2 },
  fsp: { label: 'FS+', color: '#1e88e5', rank: 3 },
  fsd: { label: 'FSD', color: '#e040fb', rank: 4 },
  fsdp: { label: 'FSD+', color: '#c51dd4', rank: 5 },
}

const FlagPill: FC<{ label: string; color: string }> = ({ label, color }) => (
  <span
    className="rounded-full px-2 text-xs leading-relaxed text-white shadow-[0.0625rem_0.125rem_0_0_#0b38714D] select-none"
    style={{ backgroundColor: color }}
  >
    {label}
  </span>
)

const RatingCalculatorComboFlagCell: FC<{
  row: Row<Entry>
}> = memo(({ row }) => {
  const { comboFlag } = row.original
  if (!comboFlag) return null
  return <FlagPill label={COMBO_FLAG_CONFIG[comboFlag].label} color={COMBO_FLAG_CONFIG[comboFlag].color} />
})
RatingCalculatorComboFlagCell.displayName = 'memo(RatingCalculatorComboFlagCell)'

const RatingCalculatorSyncFlagCell: FC<{
  row: Row<Entry>
}> = memo(({ row }) => {
  const { syncFlag } = row.original
  if (!syncFlag) return null
  return <FlagPill label={SYNC_FLAG_CONFIG[syncFlag].label} color={SYNC_FLAG_CONFIG[syncFlag].color} />
})
RatingCalculatorSyncFlagCell.displayName = 'memo(RatingCalculatorSyncFlagCell)'

const RatingCalculatorAchievementRateCell: FC<{
  row: Row<Entry>
}> = ({ row }) => (
  <span className="font-sans tracking-wide tabular-nums">{row.original.achievementRate.toFixed(4)}%</span>
)

const RatingCalculatorTable: FC<TableProps> = (props: TableProps) => (
  <Table {...props} size="small" className="rounded-lg w-full min-w-2xl" style={{ borderCollapse: 'separate' }} />
)

function RatingCalculatorTableBody({ ref, ...props }: TableBodyProps & { ref?: React.Ref<HTMLTableSectionElement> }) {
  return <TableBody {...props} ref={ref} />
}

const RatingCalculatorTableRow: FC<ItemProps<Row<Entry>>> = ({ item, ...props }) => (
  <TableRow
    {...props}
    className={clsx(
      'tabular-nums w-full',
      {
        b15: 'bg-amber-200',
        b35: 'bg-cyan-200',
        none: undefined,
      }[item.original.includedIn ?? 'none'],
    )}
  />
)

function RatingCalculatorScroller({ ref, ...props }: ScrollerProps & { ref?: React.Ref<HTMLDivElement> }) {
  return <TableContainer component={TransparentPaper} {...props} ref={ref} />
}

const RatingCalculatorRatingCell: FC<{
  row: Row<Entry>
}> = ({ row }) => (
  <span className="font-sans tabular-nums">{row.original.rating ? row.original.rating.ratingAwardValue : '-'}</span>
)

const RatingCalculatorTableRowContent: FC<{
  row: Row<Entry>
}> = ({ row }) => {
  return (
    <>
      {row.getVisibleCells().map((cell) => {
        return (
          <TableCell
            key={cell.id}
            {...(
              cell.column.columnDef.meta as {
                cellProps?: Record<string, unknown>
              }
            )?.cellProps}
            style={{ width: cell.column.getSize() }}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        )
      })}
    </>
  )
}

function RatingCalculatorTableContent({ compactMode, showOnlyB50 }: { compactMode: boolean; showOnlyB50: boolean }) {
  const { allEntries } = useRatingEntries()
  const { modifyEntries } = useRatingCalculatorContext()
  const { t } = useTranslation(['rating-calculator'])

  const [sorting, setSorting] = useState<SortingState>([
    { id: 'includedIn', desc: true },
    { id: 'rating', desc: true },
  ])

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'chart',
        header: t('rating-calculator:table.headers.chart'),
        cell: ({ row }) => (
          <SheetListItem
            sheet={row.original.sheet}
            SheetDialogContentProps={{
              currentAchievementRate: row.original.achievementRate,
            }}
            SheetListItemContentProps={{
              enableSheetImage: !compactMode,
              SheetTitleProps: {
                enableVersion: false,
                className: 'flex-col',
              },
              ListItemTextProps: {
                className: clsx('!my-0', compactMode ? '!ml-0' : '!ml-1'),
              },
            }}
          />
        ),
        meta: {
          cellProps: {
            padding: 'none',
          },
        },
        size: 700,
        minSize: 400,
      }),
      columnHelper.accessor('includedIn', {
        id: 'includedIn',
        header: t('rating-calculator:table.headers.included-in'),
        cell: RatingCalculatorIncludedInCell,
        size: 50,
        minSize: 100,
      }),
      columnHelper.accessor('comboFlag', {
        id: 'comboFlag',
        header: t('rating-calculator:table.headers.combo'),
        cell: RatingCalculatorComboFlagCell,
        size: 50,
        minSize: 50,
        sortingFn: (a, b) => {
          const rankA = a.original.comboFlag ? COMBO_FLAG_CONFIG[a.original.comboFlag].rank : 0
          const rankB = b.original.comboFlag ? COMBO_FLAG_CONFIG[b.original.comboFlag].rank : 0
          return rankA - rankB
        },
      }),
      columnHelper.accessor('syncFlag', {
        id: 'syncFlag',
        header: t('rating-calculator:table.headers.sync'),
        cell: RatingCalculatorSyncFlagCell,
        size: 50,
        minSize: 50,
        sortingFn: (a, b) => {
          const rankA = a.original.syncFlag ? SYNC_FLAG_CONFIG[a.original.syncFlag].rank : 0
          const rankB = b.original.syncFlag ? SYNC_FLAG_CONFIG[b.original.syncFlag].rank : 0
          return rankA - rankB
        },
      }),
      columnHelper.accessor('achievementRate', {
        id: 'achievementRate',
        header: t('rating-calculator:table.headers.achievement-rate'),
        cell: RatingCalculatorAchievementRateCell,
        size: 100,
        minSize: 150,
      }),
      columnHelper.accessor('rating.ratingAwardValue', {
        id: 'rating',
        header: t('rating-calculator:table.headers.rating'),
        cell: RatingCalculatorRatingCell,
        size: 50,
        minSize: 100,
        sortingFn: (a, b) => {
          if (!a.original.rating) return -1
          if (!b.original.rating) return 1
          return a.original.rating.ratingAwardValue - b.original.rating.ratingAwardValue
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: t('rating-calculator:table.headers.actions'),
        cell: ({ row }) => <RatingCalculatorRowActions entry={row.original} modifyEntries={modifyEntries} />,
        size: 50,
        minSize: 100,
      }),
    ],
    [modifyEntries, compactMode, t],
  )

  const data = useMemo(() => {
    return showOnlyB50 ? allEntries.filter((entry) => entry.includedIn) : allEntries
  }, [allEntries, showOnlyB50])

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    isMultiSortEvent: () => true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  const TableComponents = useMemo(
    () =>
      ({
        Scroller: RatingCalculatorScroller,
        Table: RatingCalculatorTable,
        // oxlint-disable-next-line @typescript-eslint/no-explicit-any -- it is sort of impossible to type this
        TableHead: TableHead as any,
        TableRow: RatingCalculatorTableRow,
        TableBody: RatingCalculatorTableBody,
      }) as TableComponents<Row<Entry>>,
    [compactMode],
  )

  const getItemContent = useCallback(
    (index: number) => <RatingCalculatorTableRowContent row={table.getRowModel().rows[index]} />,
    [table],
  )

  return (
    <TableVirtuoso<Row<Entry>>
      useWindowScroll
      data={table.getRowModel().rows}
      className="w-full overflow-y-hidden"
      increaseViewportBy={2000}
      overscan={10}
      components={TableComponents}
      fixedHeaderContent={() =>
        table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              return (
                <TableCell
                  key={header.id}
                  colSpan={header.colSpan}
                  className={clsx(
                    'group bg-gray-900/5 transition whitespace-nowrap',
                    header.column.getCanSort() &&
                      'cursor-pointer select-none hover:bg-gray-900/10 active:bg-gray-900/20 leading-tight py-4',
                  )}
                  onClick={header.column.getToggleSortingHandler()}
                  style={{ width: header.getSize() }}
                >
                  {header.isPlaceholder ? null : (
                    <div>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      <div
                        className={clsx(
                          'inline-flex items-center overflow-hidden relative',
                          header.column.getIsSorted() && 'bg-gray-900/50 text-zinc-100 rounded-full',
                        )}
                      >
                        <IconMdiArrowDown
                          className={clsx(
                            'ml-1 transition mr-0.5',
                            {
                              asc: 'inline-flex rotate-180',
                              desc: 'inline-flex rotate-0',
                              none: header.column.getCanSort()
                                ? 'inline-flex opacity-0 group-hover:opacity-70'
                                : 'hidden',
                            }[(header.column.getIsSorted() as string) || 'none'],
                          )}
                        />
                        {header.column.getIsSorted() && sorting.length > 1 && (
                          <div className="inline-flex items-center justify-center text-sm px-2 font-bold bg-gray-9/50">
                            {header.column.getSortIndex() + 1}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </TableCell>
              )
            })}
          </TableRow>
        ))
      }
      itemContent={getItemContent}
    />
  )
}